// src/components/ProtocolForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Divider,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Grid,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ChevronDown, ArrowUp, ArrowDown, Trash2, Plus, Layers } from "lucide-react";

import {
  useCreateProtocolMutation,
  useUpdateProtocolMutation,
} from "../../redux/protocol/protocolApi";

const initialFormState = {
  protocolId: "",
  name: "",
  manufacturer: "",
  version: "1.0",
  // New configuration remains test-only until a separate, approved listener
  // is built. Existing tracker ports are never opened from this form.
  status: "testing",

  transportType: "tcp",
  transportPort: "",
  runtimeMode: "disabled",
  testImeis: "",

  header: "",
  lengthType: "byte",
  lengthOffset: 2,
  crcType: "x25",
  stopBit: "0d0a",
  byteOrder: "big",

  imeiEncoding: "bcd",
  imeiOffset: 0,
  imeiLength: 8,
  imeiPacketType: "01",

  notes: "",
};

const emptyDataField = {
  id: "",
  name: "",
  offset: "",
  length: 1,
  type: "uint",
  formula: "",
  unit: "",
  description: "",
};

const fieldTemplates = [
  { name: "latitude", type: "int32", length: 4, formula: "raw / 30000 / 60", unit: "degrees" },
  { name: "longitude", type: "int32", length: 4, formula: "raw / 30000 / 60", unit: "degrees" },
  { name: "speed", type: "uint8", length: 1, unit: "kmh" },
  { name: "heading", type: "uint16", length: 2, unit: "degrees" },
  { name: "altitude", type: "int16", length: 2, unit: "meters" },
  { name: "satellites", type: "uint8", length: 1 },
  { name: "battery", type: "uint8", length: 1, unit: "percent" },
  { name: "gsm_signal", type: "uint8", length: 1 },
  { name: "temperature", type: "int8", length: 1, unit: "celsius" },
  { name: "fuel", type: "uint8", length: 1, unit: "percent" },
  { name: "odometer", type: "uint32", length: 4, unit: "meters" },
  { name: "acc_status", type: "bool", length: 1 },
];

export default function ProtocolForm({ editData, isEditLoading = false, onSuccess, onCancel }) {
  const theme = useTheme();

  const [form, setForm] = useState(initialFormState);
  const [dataFields, setDataFields] = useState([]);
  const [packetTypes, setPacketTypes] = useState([]);
  const [success, setSuccess] = useState(false);
  const [validationError, setValidationError] = useState("");

  const [createProtocol, { isLoading: isCreating, error: createError }] = useCreateProtocolMutation();
  const [updateProtocol, { isLoading: isUpdating, error: updateError }] = useUpdateProtocolMutation();

  const isEditMode = !!editData;
  const isLoading = isCreating || isUpdating;
  const error = createError || updateError;

  useEffect(() => {
    if (!editData) {
      setForm(initialFormState);
      setDataFields([]);
      setPacketTypes([]);
      return;
    }

    setForm({
      protocolId: editData.protocolId || "",
      name: editData.name || "",
      manufacturer: editData.manufacturer || "",
      version: editData.version || "1.0",
      status: editData.status || "active",

      transportType: editData.transport?.type || "tcp",
      transportPort: editData.transport?.port || "",
      runtimeMode: editData.runtimeConfig?.mode || "disabled",
      testImeis: (editData.runtimeConfig?.testImeis || []).join(", "),

      header: editData.frameStructure?.header?.join(", ") || "",
      lengthType: editData.frameStructure?.lengthType || "byte",
      lengthOffset: editData.frameStructure?.lengthOffset ?? 2,
      crcType: editData.frameStructure?.crcType || "x25",
      stopBit: editData.frameStructure?.stopBit || "0d0a",
      byteOrder: editData.frameStructure?.byteOrder || "big",

      imeiEncoding: editData.imeiConfig?.encoding || "bcd",
      imeiOffset: editData.imeiConfig?.offset ?? 0,
      imeiLength: editData.imeiConfig?.length ?? 8,
      imeiPacketType: editData.imeiConfig?.packetType || "01",

      notes: editData.notes || "",
    });

    if (editData.dataFields?.length) {
      setDataFields(editData.dataFields.map((f, i) => ({ ...f, id: `field_${i}_${Date.now()}` })));
    }
    if (editData.packetTypes?.length) {
      setPacketTypes(editData.packetTypes.map((p, i) => ({ ...p, id: `pkt_${i}_${Date.now()}` })));
    } else {
      setPacketTypes([]);
    }
    if (!editData.dataFields?.length) {
      setDataFields([]);
    }
  }, [editData]);

  const sectionPaperSx = {
    p: { xs: 1.5, md: 2 },
    borderRadius: 3,
    mb: 2,
  };

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // Data fields CRUD
  const addDataField = (template = null) => {
    setDataFields((prev) => [
      ...prev,
      {
        ...emptyDataField,
        id: `field_${Date.now()}`,
        ...(template || {}),
      },
    ]);
  };

  const updateDataField = (id, key, value) => {
    setDataFields((prev) => prev.map((f) => (f.id === id ? { ...f, [key]: value } : f)));
  };

  const removeDataField = (id) => setDataFields((prev) => prev.filter((f) => f.id !== id));

  const moveField = (index, dir) => {
    setDataFields((prev) => {
      const arr = [...prev];
      const next = index + dir;
      if (next < 0 || next >= arr.length) return prev;
      [arr[index], arr[next]] = [arr[next], arr[index]];
      return arr;
    });
  };

  // Packet types CRUD
  const addPacketType = () => {
    setPacketTypes((prev) => [
      ...prev,
      { id: `pkt_${Date.now()}`, protocolId: "", name: "", description: "", requiresAck: true },
    ]);
  };
  const updatePacketType = (id, key, value) => {
    setPacketTypes((prev) => prev.map((p) => (p.id === id ? { ...p, [key]: value } : p)));
  };
  const removePacketType = (id) => setPacketTypes((prev) => prev.filter((p) => p.id !== id));

  const headerHelper = "Comma-separated hex headers (example: 7878, 7979)";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError("");

    const incompleteDataField = dataFields.find((f) => {
      const hasAnyValue = Boolean(
        String(f?.name || "").trim() ||
        String(f?.offset ?? "").trim() ||
        String(f?.length ?? "").trim() ||
        String(f?.type || "").trim() ||
        String(f?.unit || "").trim() ||
        String(f?.formula || "").trim() ||
        String(f?.description || "").trim()
      );
      if (!hasAnyValue) return false;
      return !String(f?.name || "").trim() || f?.offset === "" || f?.offset === null || f?.offset === undefined;
    });

    if (incompleteDataField) {
      setValidationError("Each data field must have at least Name and Offset before saving.");
      return;
    }

    const incompletePacketType = packetTypes.find((p) => {
      const hasAnyValue = Boolean(
        String(p?.protocolId || "").trim() ||
        String(p?.name || "").trim() ||
        String(p?.description || "").trim()
      );
      if (!hasAnyValue) return false;
      return !String(p?.protocolId || "").trim() || !String(p?.name || "").trim();
    });

    if (incompletePacketType) {
      setValidationError("Each packet type must have both Packet ID and Name before saving.");
      return;
    }

    const cleanedDataFields = dataFields
      .filter((f) => f.name && f.offset !== "" && f.offset !== null)
      .map(({ id, ...rest }) => ({
        name: rest.name.trim(),
        offset: Number(rest.offset),
        length: Number(rest.length) || 1,
        type: rest.type || "uint",
        formula: rest.formula?.trim() || undefined,
        unit: rest.unit?.trim() || undefined,
        description: rest.description?.trim() || undefined,
      }));

    const cleanedPacketTypes = packetTypes
      .map(({ id, ...rest }) => ({
        protocolId: rest.protocolId,
        name: rest.name,
        description: rest.description || undefined,
        requiresAck: rest.requiresAck ?? true,
      }))
      .filter((p) => p.protocolId && p.name);

    const protocolData = {
      protocolId: form.protocolId,
      name: form.name,
      manufacturer: form.manufacturer,
      version: form.version,
      status: form.status,

      transport: {
        type: form.transportType,
        port: form.transportPort ? parseInt(form.transportPort, 10) : undefined,
      },
      runtimeConfig: {
        mode: form.runtimeMode,
        testImeis: form.testImeis.split(",").map((imei) => imei.trim()).filter(Boolean),
      },

      frameStructure: {
        header: form.header
          .split(",")
          .map((h) => h.trim().toLowerCase())
          .filter(Boolean),
        lengthType: form.lengthType,
        lengthOffset: parseInt(form.lengthOffset, 10),
        crcType: form.crcType,
        stopBit: form.stopBit,
        byteOrder: form.byteOrder,
      },

      imeiConfig: {
        encoding: form.imeiEncoding,
        offset: parseInt(form.imeiOffset, 10),
        length: parseInt(form.imeiLength, 10),
        packetType: form.imeiPacketType,
      },

      dataFields: cleanedDataFields,
      packetTypes: cleanedPacketTypes,

      notes: form.notes,
    };

    try {
      if (isEditMode) {
        await updateProtocol({ protocolId: form.protocolId, ...protocolData }).unwrap();
      } else {
        await createProtocol(protocolData).unwrap();
      }
      setSuccess(true);
      onSuccess?.();
    } catch (err) {
      console.error("Failed to save protocol:", err);
    }
  };

  if (success) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Protocol {isEditMode ? "updated" : "created"} successfully!
        </Alert>
        <Button
          variant="contained"
          onClick={() => {
            setSuccess(false);
            setForm(initialFormState);
            setDataFields([]);
            setPacketTypes([]);
          }}
          sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
        >
          {isEditMode ? "Done" : "Create Another"}
        </Button>
      </Paper>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 900, fontSize: 18 }}>
          {isEditMode ? "Edit Protocol" : "Create New Protocol"}
        </Typography>
        <Chip
          size="small"
          label={isEditMode ? "Edit Mode" : "New"}
          variant="outlined"
          sx={{ fontWeight: 900 }}
        />
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Safe setup:</strong> this screen saves and tests a protocol only.
        It will not change current vehicle tracking, existing ports, or live devices.
        Fill one section at a time, then use <strong>Tester</strong> with a real packet
        before requesting live activation.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.data?.error || error.message || "Failed to save protocol"}
        </Alert>
      )}

      {validationError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {isEditLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading saved protocol details...
        </Alert>
      )}

      {/* BASIC INFO */}
      <Paper variant="outlined" sx={sectionPaperSx}>
        <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Basic Information</Typography>

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Protocol ID *"
              value={form.protocolId}
              onChange={handleChange("protocolId")}
              placeholder="e.g., gt06, teltonika_v1"
              required
              disabled={isEditMode}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Name *"
              value={form.name}
              onChange={handleChange("name")}
              placeholder="e.g., GT06 Protocol"
              required
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Manufacturer"
              value={form.manufacturer}
              onChange={handleChange("manufacturer")}
              placeholder="e.g., Concox, Teltonika"
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Version"
              value={form.version}
              onChange={handleChange("version")}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Status"
              value={form.status}
              onChange={handleChange("status")}
              fullWidth
              size="small"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="testing">Testing</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* TRANSPORT */}
      <Paper variant="outlined" sx={sectionPaperSx}>
        <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Transport Layer</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Where this tracker normally sends data. Port is only a note in safe test mode; this form never opens a port.
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Type"
              value={form.transportType}
              onChange={handleChange("transportType")}
              fullWidth
              size="small"
            >
              <MenuItem value="tcp">TCP</MenuItem>
              <MenuItem value="udp">UDP</MenuItem>
              <MenuItem value="http">HTTP</MenuItem>
              <MenuItem value="mqtt">MQTT</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Port"
              type="number"
              value={form.transportPort}
              onChange={handleChange("transportPort")}
              placeholder="e.g., 5001"
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* SAFE TEST CONNECTION */}
      <Paper variant="outlined" sx={sectionPaperSx}>
        <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Safe Test Connection (Optional)</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Keep Disabled while filling the protocol. Canary only accepts the test tracker IDs below on a separate new port.
          It does not save location or affect current tracking.
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={5}>
            <TextField select label="Connection mode" value={form.runtimeMode} onChange={handleChange("runtimeMode")} fullWidth size="small">
              <MenuItem value="disabled">Disabled - save and test packet only</MenuItem>
              <MenuItem value="canary">Canary - separate test port, test IDs only</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={7}>
            <TextField
              label="Allowed test tracker IMEI(s)"
              value={form.testImeis}
              onChange={handleChange("testImeis")}
              placeholder="e.g., 123456789012345, 987654321098765"
              helperText="Required for Canary. Separate multiple IMEIs with a comma."
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* FRAME STRUCTURE */}
      <Paper variant="outlined" sx={sectionPaperSx}>
        <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Packet Start, Length and Check</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Copy these values from the tracker protocol PDF. Header and stop bytes are hexadecimal, for example 7878 and 0d0a.
        </Typography>

        <TextField
          label="Header *"
          value={form.header}
          onChange={handleChange("header")}
          placeholder="7878, 7979"
          required
          fullWidth
          size="small"
          helperText={headerHelper}
          sx={{ mb: 1.5 }}
        />

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Length Type"
              value={form.lengthType}
              onChange={handleChange("lengthType")}
              fullWidth
              size="small"
            >
              <MenuItem value="byte">Byte (1 byte)</MenuItem>
              <MenuItem value="word">Word (2 bytes)</MenuItem>
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="fixed">Fixed</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Length Offset"
              type="number"
              value={form.lengthOffset}
              onChange={handleChange("lengthOffset")}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="CRC Type"
              value={form.crcType}
              onChange={handleChange("crcType")}
              fullWidth
              size="small"
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="x25">CRC-X25</MenuItem>
              <MenuItem value="xmodem">XMODEM</MenuItem>
              <MenuItem value="modbus">Modbus</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Stop Bit (hex)"
              value={form.stopBit}
              onChange={handleChange("stopBit")}
              placeholder="0d0a"
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Byte Order"
              value={form.byteOrder}
              onChange={handleChange("byteOrder")}
              fullWidth
              size="small"
            >
              <MenuItem value="big">Big Endian</MenuItem>
              <MenuItem value="little">Little Endian</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* IMEI CONFIG */}
      <Paper variant="outlined" sx={sectionPaperSx}>
        <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Tracker ID (IMEI) Position</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Tell the tester where the device ID begins. Offset starts at 0, which means the first byte.
        </Typography>

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Encoding"
              value={form.imeiEncoding}
              onChange={handleChange("imeiEncoding")}
              fullWidth
              size="small"
            >
              <MenuItem value="bcd">BCD</MenuItem>
              <MenuItem value="ascii">ASCII</MenuItem>
              <MenuItem value="hex">Hex</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Login Packet Type (hex)"
              value={form.imeiPacketType}
              onChange={handleChange("imeiPacketType")}
              placeholder="01"
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Offset"
              type="number"
              value={form.imeiOffset}
              onChange={handleChange("imeiOffset")}
              fullWidth
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Length (bytes)"
              type="number"
              value={form.imeiLength}
              onChange={handleChange("imeiLength")}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* DATA FIELDS */}
      <Paper variant="outlined" sx={sectionPaperSx}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 900 }}>
            Data Fields / What each byte means <Typography component="span" color="text.secondary">({dataFields.length})</Typography>
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Layers size={16} />}
              onClick={() => addDataField(fieldTemplates[Math.floor(Math.random() * fieldTemplates.length)])}
              sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
            >
              Quick Template
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => addDataField()}
              sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
            >
              Add Field
            </Button>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 1.5 }}>
          {fieldTemplates.slice(0, 10).map((t) => (
            <Chip
              key={t.name}
              label={t.name}
              onClick={() => addDataField(t)}
              variant="outlined"
              size="small"
              sx={{ fontWeight: 800 }}
            />
          ))}
        </Stack>

        {dataFields.length === 0 ? (
          <Alert severity="info">No data fields yet. Click “Add Field” or tap a template chip.</Alert>
        ) : (
          <Stack spacing={1}>
            {dataFields.map((f, index) => (
              <Accordion key={f.id} defaultExpanded>
                <AccordionSummary expandIcon={<ChevronDown size={18} />}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                    <Chip size="small" label={`#${index + 1}`} sx={{ fontWeight: 900 }} />
                    <Typography sx={{ fontWeight: 900 }} noWrap>
                      {f.name || "(unnamed field)"}
                    </Typography>

                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Move up">
                      <span>
                        <IconButton size="small" onClick={(e) => (e.stopPropagation(), moveField(index, -1))} disabled={index === 0}>
                          <ArrowUp size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => (e.stopPropagation(), moveField(index, 1))}
                          disabled={index === dataFields.length - 1}
                        >
                          <ArrowDown size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        onClick={(e) => (e.stopPropagation(), removeDataField(f.id))}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Name *"
                        value={f.name}
                        onChange={(e) => updateDataField(f.id, "name", e.target.value)}
                        fullWidth
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        label="Offset"
                        type="number"
                        value={f.offset}
                        onChange={(e) => updateDataField(f.id, "offset", e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        label="Length"
                        type="number"
                        value={f.length}
                        onChange={(e) => updateDataField(f.id, "length", e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        select
                        label="Type"
                        value={f.type}
                        onChange={(e) => updateDataField(f.id, "type", e.target.value)}
                        fullWidth
                        size="small"
                      >
                        {[
                          "uint",
                          "int",
                          "uint8",
                          "int8",
                          "uint16",
                          "int16",
                          "uint32",
                          "int32",
                          "float",
                          "double",
                          "bool",
                          "bcd",
                          "ascii",
                          "hex",
                        ].map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        label="Unit"
                        value={f.unit}
                        onChange={(e) => updateDataField(f.id, "unit", e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Formula (optional)"
                        value={f.formula}
                        onChange={(e) => updateDataField(f.id, "formula", e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="raw / 30000 / 60"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Description"
                        value={f.description}
                        onChange={(e) => updateDataField(f.id, "description", e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </Paper>

      {/* PACKET TYPES */}
      <Paper variant="outlined" sx={sectionPaperSx}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 900 }}>
            Packet Types <Typography component="span" color="text.secondary">({packetTypes.length})</Typography>
          </Typography>
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={addPacketType}
            sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
          >
            Add Packet Type
          </Button>
        </Stack>

        {packetTypes.length === 0 ? (
          <Alert severity="info">
            No packet types yet. Common: 01 (login), 12 (location), 13 (heartbeat)
          </Alert>
        ) : (
          <Stack spacing={1}>
            {packetTypes.map((p, idx) => (
              <Paper
                key={p.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.divider, 0.9),
                }}
              >
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
                  <Chip
                    size="small"
                    label={`#${idx + 1}`}
                    sx={{ fontWeight: 900, alignSelf: { xs: "flex-start", md: "center" } }}
                  />

                  <TextField
                    label="Packet ID (hex)"
                    value={p.protocolId}
                    onChange={(e) => updatePacketType(p.id, "protocolId", e.target.value)}
                    placeholder="e.g., 01"
                    size="small"
                    sx={{ width: { xs: "100%", md: 160 } }}
                  />

                  <TextField
                    label="Name"
                    value={p.name}
                    onChange={(e) => updatePacketType(p.id, "name", e.target.value)}
                    placeholder="e.g., login"
                    size="small"
                    sx={{ width: { xs: "100%", md: 220 } }}
                  />

                  <TextField
                    label="Description"
                    value={p.description}
                    onChange={(e) => updatePacketType(p.id, "description", e.target.value)}
                    placeholder="optional"
                    size="small"
                    fullWidth
                  />

                  <Tooltip title="Remove packet type">
                    <IconButton
                      onClick={() => removePacketType(p.id)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {/* NOTES */}
      <Paper variant="outlined" sx={sectionPaperSx}>
        <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Notes</Typography>
        <TextField
          label="Notes"
          value={form.notes}
          onChange={handleChange("notes")}
          multiline
          minRows={3}
          fullWidth
          size="small"
          placeholder="Any additional notes about this protocol…"
        />
      </Paper>

      {/* ACTIONS */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.5, md: 2 },
          borderRadius: 3,
          position: "sticky",
          bottom: 12,
          bgcolor: alpha(theme.palette.background.paper, 0.9),
          backdropFilter: "blur(10px)",
          borderColor: alpha(theme.palette.divider, 0.9),
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900 }}>
              {isEditMode ? "Update Protocol" : "Create Protocol"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Make sure headers & offsets are correct before saving.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-end", sm: "flex-end" }}>
            {onCancel && (
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
            >
              {isLoading ? "Saving..." : isEditMode ? "Update Protocol" : "Create Protocol"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
